"use client";

import { c as _c } from "react/compiler-runtime";
import { darken, useTheme } from "@mui/material";
import React from "react";
import { ConfigContext } from "#/context";
import { MainAppBar } from "#/features/appBar/ui/MainAppBar";
import { CodePanel } from "#/features/codeRunner/ui/CodePanel";
import { OutputPanel } from "#/features/output/ui/OutputPanel";
import { PlaygroundViewProvider } from "#/features/playground/context/PlaygroundViewContext";
import { usePlaygroundRuntimeRelease } from "#/features/playground/hooks/usePlaygroundRuntimeRelease";
import { MobilePlayground } from "#/features/playground/ui/MobilePlayground";
import { ProjectPanel } from "#/features/project/ui/ProjectPanel";
import { TreeViewPanel } from "#/features/treeViewer/ui/TreeViewPanel";
import { useAppConfig, useHasMounted } from "#/shared/hooks";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { PageScrollContainer } from "#/shared/ui/templates/PageScrollContainer";
import type { SplitPanelsLayoutProps } from "#/shared/ui/templates/SplitPanelsLayout/SplitPanelsLayout";
import { SplitPanelsLayout } from "#/shared/ui/templates/SplitPanelsLayout/SplitPanelsLayout";
import { SplitPanelsLayoutSkeleton } from "#/shared/ui/templates/SplitPanelsLayout/SplitPanelsLayoutSkeleton";
type DesktopWrapperProps = SplitPanelsLayoutProps;
const DesktopWrapper: React.FC<DesktopWrapperProps> = t0 => {
  const $ = _c(6);
  const {
    TopLeft,
    BottomLeft,
    TopRight,
    BottomRight
  } = t0;
  const hasMounted = useHasMounted();
  if (!hasMounted) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SplitPanelsLayoutSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== BottomLeft || $[2] !== BottomRight || $[3] !== TopLeft || $[4] !== TopRight) {
    t1 = <SplitPanelsLayout component="main" TopLeft={TopLeft} BottomLeft={BottomLeft} TopRight={TopRight} BottomRight={BottomRight} />;
    $[1] = BottomLeft;
    $[2] = BottomRight;
    $[3] = TopLeft;
    $[4] = TopRight;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};

/** Playground shell shared by Pages `/playground` and App pilot routes. */
export const PlaygroundPageView: React.FC = () => {
  const $ = _c(17);
  const theme = useTheme();
  const isMobile = useMobileLayout();
  usePlaygroundRuntimeRelease();
  const {
    data: t0
  } = useAppConfig();
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const data = t1;
  let t2;
  if ($[2] !== isMobile) {
    t2 = isMobile ? {
      overflow: {
        x: "hidden",
        y: "hidden"
      }
    } : {
      scrollbars: {
        autoHide: "scroll"
      },
      overflow: {
        x: "hidden"
      }
    };
    $[2] = isMobile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== theme.palette.background.default) {
    t3 = darken(theme.palette.background.default, 0.1);
    $[4] = theme.palette.background.default;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      height: "100vh",
      background: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isMobile) {
    t5 = isMobile ? <PlaygroundViewProvider><MainAppBar toolbarVariant="dense" /><MobilePlayground /></PlaygroundViewProvider> : <><MainAppBar toolbarVariant="dense" /><DesktopWrapper TopLeft={ProjectPanel} BottomLeft={CodePanel} TopRight={TreeViewPanel} BottomRight={OutputPanel} /></>;
    $[8] = isMobile;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t4 || $[12] !== t5) {
    t6 = <PageScrollContainer isPage={true} options={t2} style={t4}>{t5}</PageScrollContainer>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== data || $[15] !== t6) {
    t7 = <ConfigContext.Provider value={data}>{t6}</ConfigContext.Provider>;
    $[14] = data;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};